import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MonitorPlay, Search, ChevronDown, Calendar, Book, ArrowRight, Users, CheckSquare } from "lucide-react";
import { getAllCourses } from "../../../services/course.service";
import { useMeetingsV2 } from "../../../context/MeetingV2Context";
import { useUtilityContext } from "../../../context/UtilityContext";
import { getCardColor } from "../../../utils/courseHelpers";
import {
  pageTitle, badgeBase, emptyState,
  accentBarSection, surfaceMuted,
  motionFadeUp, motionStaggerChild, motionHoverLift,
} from "../../../lib/design-system";
import { cn } from "../../../lib/utils";
import { CourseGridSkeleton } from "../../StudentDashboard/Components/ui/Skeleton";

const TeacherCourses = () => {
  const [coursesData, setCoursesData] = useState({
    teacher: {},
    courses: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterSemester, setFilterSemester] = useState("All");
  const [filterYear, setFilterYear] = useState("All");

  const { getMeetingsForCourse, fetchMeetingsForCourse } = useMeetingsV2();
  const { setActiveSection, setSelectedCourseId } = useUtilityContext();

  const handleCourseClick = (courseId) => {
    setSelectedCourseId(courseId);
    setActiveSection("Courseware");
  };

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const data = await getAllCourses();
        setCoursesData(data);
        setLoading(false);
      } catch (err) {
        setError("Failed to load courses. Please try again later.");
        setLoading(false);
      }
    };
    fetchCourses();
  }, []);

  useEffect(() => {
    if (coursesData.courses && coursesData.courses.length > 0) {
      const uniqueCourseIds = [...new Set(coursesData.courses.map(c => c._id).filter(Boolean))];
      uniqueCourseIds.forEach(courseId => {
        fetchMeetingsForCourse(courseId).catch(() => {});
      });
    }
  }, [coursesData.courses, fetchMeetingsForCourse]);

  const getSemesterInfo = (startDate) => {
    const date = new Date(startDate);
    const month = date.getMonth();
    const year = date.getFullYear();
    let season = (month >= 0 && month <= 4) ? "Spring" : "Fall";
    return { season, year: year.toString() };
  };

  const groupCoursesBySemester = (courses) => {
    if (!courses || courses.length === 0) return [];
    const semesterMap = {};

    courses.forEach((course) => {
      if (!course.semester || !course.semester.startDate) return;
      const { season, year } = getSemesterInfo(course.semester.startDate);
      const semesterId = `${season}-${year}`;

      if (!semesterMap[semesterId]) {
        semesterMap[semesterId] = {
          season,
          year,
          semesterId: course.semester._id,
          courses: [],
        };
      }

      semesterMap[semesterId].courses.push({
        ...course,
        students: course.students ? course.students.length : 0,
      });
    });

    return Object.values(semesterMap).sort((a, b) => {
      if (b.year !== a.year) return parseInt(b.year) - parseInt(a.year);
      const seasonOrder = { Spring: 0, Fall: 2 };
      return seasonOrder[a.season] - seasonOrder[b.season];
    });
  };

  const findLiveMeetingForSemester = (semesterCourses) => {
    if (!semesterCourses) return null;

    for (const course of semesterCourses) {
      if (!course?._id) continue;
      const courseMeetings = getMeetingsForCourse(course._id) || [];
      const liveMeeting = courseMeetings.find(
        (meeting) =>
          meeting.course === course._id &&
          meeting.status === "live"
      );
      if (liveMeeting) return liveMeeting;
    }

    return null;
  };

  const semesters = groupCoursesBySemester(coursesData.courses);

  const filteredSemesters = semesters.filter((semester) => {
    const semesterMatch = filterSemester === "All" || semester.season === filterSemester;
    const yearMatch = filterYear === "All" || semester.year === filterYear;
    return semesterMatch && yearMatch;
  });

  const filteredCourses = filteredSemesters.map((semester) => ({
    ...semester,
    courses: semester.courses.filter((course) =>
      course.title.toLowerCase().includes(searchTerm.toLowerCase())
    ),
  }));

  const uniqueYears = [...new Set(semesters.map((semester) => semester.year))];

  if (loading) {
    return (
      <div className="px-3 md:px-4 py-4 md:py-8 bg-white dark:bg-gray-900 h-full">
        <CourseGridSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-3 md:px-4 py-4 md:py-8 bg-white dark:bg-gray-900 h-full">
        <div className={cn(emptyState, "text-red-500 dark:text-red-400")}>
          <Book className="h-10 w-10 mx-auto mb-3 text-red-400 dark:text-red-500" />
          <h3 className="text-lg font-bold mb-2">Error Loading Courses</h3>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      {...motionFadeUp}
      className="px-3 md:px-4 py-4 md:py-8 bg-white dark:bg-gray-900 overflow-y-auto h-full"
    >
      {/* Page header */}
      <h1 className={cn(pageTitle, "mb-2")}>My Courses</h1>
      <div className={cn(accentBarSection, "mb-6")} />

      {/* Search and Filter */}
      <div className="flex flex-col md:flex-row md:flex-wrap gap-3 md:gap-4 mb-6 md:mb-8 w-full md:justify-between md:items-center">
        <div className={cn(surfaceMuted, "flex items-center rounded-sm px-3 md:px-4 py-2 w-full md:w-1/3 border border-gray-200 dark:border-gray-700")}>
          <Search className="h-4 w-4 md:h-5 md:w-5 text-accent1 mr-2" />
          <input
            type="text"
            placeholder="Search courses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="bg-transparent outline-none w-full text-sm md:text-base text-gray-700 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400"
          />
        </div>
        <div className="flex gap-2 w-full md:w-auto">
          <div className="relative flex-1 md:flex-none md:w-fit">
            <select
              value={filterSemester}
              onChange={(e) => setFilterSemester(e.target.value)}
              className={cn(surfaceMuted, "appearance-none rounded-sm px-3 md:px-4 py-2 text-sm md:text-base text-gray-700 dark:text-gray-200 pr-8 md:pr-10 border border-gray-200 dark:border-gray-700 w-full")}
            >
              <option value="All">All Semesters</option>
              <option value="Spring">Spring</option>
              <option value="Fall">Fall</option>
            </select>
            <ChevronDown className="absolute right-2 md:right-3 top-2.5 md:top-3 h-4 w-4 md:h-5 md:w-5 text-gray-500 dark:text-gray-400 pointer-events-none" />
          </div>
          <div className="relative flex-1 md:flex-none md:w-fit">
            <select
              value={filterYear}
              onChange={(e) => setFilterYear(e.target.value)}
              className={cn(surfaceMuted, "appearance-none rounded-sm px-3 md:px-4 py-2 text-sm md:text-base text-gray-700 dark:text-gray-200 pr-8 md:pr-10 border border-gray-200 dark:border-gray-700 w-full")}
            >
              <option value="All">All Years</option>
              {uniqueYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
            <Calendar className="absolute right-2 md:right-3 top-2.5 md:top-3 h-4 w-4 md:h-5 md:w-5 text-gray-500 dark:text-gray-400 pointer-events-none" />
          </div>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {filteredCourses.length === 0 ? (
          <motion.div
            key="empty"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
            className={emptyState}
          >
            <Book className="h-10 w-10 mx-auto mb-3 text-gray-400 dark:text-gray-500" />
            <h3 className="text-lg font-bold text-gray-700 dark:text-gray-200 mb-2">No courses found</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Try adjusting your search or filter criteria.</p>
          </motion.div>
        ) : (
          <motion.div
            key="courses"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
          >
            {filteredCourses.map((semester, semesterIndex) => {
              const activeMeeting = findLiveMeetingForSemester(semester.courses);

              return (
                <motion.div
                  key={semester.semesterId || semesterIndex}
                  {...motionStaggerChild(semesterIndex)}
                  className="mb-8 md:mb-12"
                >
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 md:mb-6 gap-3">
                    <div className="flex gap-2 md:gap-3 flex-wrap">
                      <span className={cn(badgeBase, "text-accent1 bg-accent1/10 border-accent1/30")}>
                        {semester.season}
                      </span>
                      <span className={cn(badgeBase, "text-accent1 bg-accent1/10 border-accent1/30")}>
                        {semester.year}
                      </span>
                    </div>
                    {activeMeeting ? (
                      <a
                        href={activeMeeting.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-red-600 dark:bg-red-500 text-white px-4 md:px-6 py-1.5 md:py-2 rounded-sm text-xs md:text-sm hover:bg-red-700 dark:hover:bg-red-600 transition-colors flex items-center gap-2 animate-pulse w-full sm:w-auto justify-center"
                      >
                        <MonitorPlay className="h-4 w-4 md:h-5 md:w-5" />
                        Join Live
                      </a>
                    ) : (
                      <button
                        disabled
                        className="bg-gray-400 dark:bg-gray-600 text-white dark:text-gray-300 px-4 md:px-6 py-1.5 md:py-2 rounded-sm text-xs md:text-sm flex items-center gap-2 cursor-not-allowed w-full sm:w-auto justify-center"
                      >
                        <MonitorPlay className="h-4 w-4 md:h-5 md:w-5" />
                        No Live Lecture
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                    {semester.courses.map((course, cardIndex) => {
                      const cardColor = getCardColor(course.title || course.courseCode);

                      return (
                        <motion.div
                          key={course._id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.4, delay: 0.6 + cardIndex * 0.1 }}
                          whileHover={motionHoverLift}
                        >
                          <button
                            onClick={() => handleCourseClick(course._id)}
                            className="group relative flex flex-col justify-between rounded-sm border border-gray-200 dark:border-gray-700 shadow-sm dark:shadow-none hover:shadow-md transition-all duration-200 overflow-hidden bg-white dark:bg-gray-800 w-full text-left"
                          >
                            {/* Color Banner with diagonal gradient */}
                            <div
                              className="relative h-20 sm:h-24 w-full overflow-hidden"
                              style={{
                                background: `linear-gradient(135deg, ${cardColor} 0%, ${cardColor} 60%, ${cardColor}cc 60%, ${cardColor}99 100%)`,
                              }}
                            >
                              <div
                                className="absolute inset-0"
                                style={{
                                  background: `linear-gradient(135deg, transparent 50%, rgba(255,255,255,0.08) 50%)`,
                                }}
                              />
                              {/* Course code */}
                              <div className="absolute bottom-2.5 left-3 sm:left-4">
                                <span className="text-white text-lg sm:text-xl font-bold tracking-wide drop-shadow-sm">
                                  {course.courseCode || "\u2014"}
                                </span>
                              </div>
                              {/* Semester badge */}
                              <span className="absolute top-2.5 right-2.5 px-2.5 py-0.5 bg-white/20 backdrop-blur-sm text-white text-xs font-medium rounded-sm border border-white/10">
                                {`${semester.season} ${semester.year}`}
                              </span>
                            </div>

                            {/* Course Details */}
                            <div className="p-3 sm:p-4 flex-1">
                              <h3 className="text-base font-bold text-gray-900 dark:text-white group-hover:text-accent1 dark:group-hover:text-accent1 transition-colors line-clamp-2">
                                {course.title}
                              </h3>
                              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1.5 line-clamp-2">
                                {course.aboutCourse}
                              </p>
                            </div>

                            {/* Footer */}
                            <div className="px-3 sm:px-4 py-2.5 border-t border-gray-200 dark:border-gray-700">
                              <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                                <div className="flex items-center space-x-3">
                                  <div className="flex items-center space-x-1">
                                    <Users className="h-3 w-3" />
                                    <span>{course?.studentCount || course.students || 0} Students</span>
                                  </div>
                                  <div className="flex items-center space-x-1">
                                    <Book className="h-3 w-3" />
                                    <span>{course.totalLectureCount || 0} Lec.</span>
                                  </div>
                                  <div className="flex items-center space-x-1">
                                    <CheckSquare className="h-3 w-3" />
                                    <span>{course.assignments?.length || 0} Assign.</span>
                                  </div>
                                </div>
                                <ArrowRight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                              </div>
                            </div>
                          </button>
                        </motion.div>
                      );
                    })}
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default TeacherCourses;
